import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Banknote,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock3,
  CreditCard,
  Home,
  ImageOff,
  Leaf,
  LocateFixed,
  Lock,
  MapPin,
  Moon,
  Navigation,
  PackageCheck,
  ShieldCheck,
  ShoppingCart,
  Sun,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import DecorLeaf from '../components/DecorLeaf';
import { SkeletonBlock } from '../components/Skeleton';
import QuoteSummary from '../components/QuoteSummary';
import usePageMetadata from '../hooks/usePageMetadata';
import useCurrentLocation from '../hooks/useCurrentLocation';
import useCheckoutQuote from '../hooks/useCheckoutQuote';
import { useCart } from '../context/CartContext';
import { useCouponIntent } from '../context/CouponIntentContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import * as addressesApi from '../api/endpoints/addresses';
import * as configApi from '../api/endpoints/config';
import * as ordersApi from '../api/endpoints/orders';
import { getCurrentSessionId } from '../api/client';
import { RAZORPAY_CHECKOUT_SRC } from '../config/api';
import { couponResult } from '../utils/couponPresentation';
import { formatRupees } from '../utils/offerPresentation';
import {
  SLOT_CONFLICT_CODES,
  attemptFingerprint,
  buildOrderPayload,
  codRestriction,
  codUnavailableMessage,
  isConfirmedPayment,
  isUncertainOutcome,
  pendingOrderOutcome,
  recoveryDecision,
  retryDelayMs,
  quoteChangeReason,
  REFUND_MESSAGES,
} from '../utils/orderPlacement';
import { reconcileCart, isCommittedSale, splitGroupOutcome, markGroupReconciled, isGroupReconciled } from '../utils/splitCartReconciliation';
import { focusRing, homeRail, inputClass } from '../styles/ui';

// Human-readable messages for the documented order-placement error codes.
const ERROR_MESSAGES = {
  PROFILE_INCOMPLETE: 'Please complete your name, phone and a delivery address before placing an order.',
  PINCODE_UNSERVICEABLE: "We don't deliver to this pincode yet. Please try a different address.",
  DELIVERY_COORDINATES_REQUIRED: 'Please choose a pinned delivery location for this address.',
  PRODUCT_NOT_FOUND: 'Some items in your cart are no longer available.',
  CATCH_WEIGHT_REQUIRES_COD: 'One or more items have variable weight and must be paid on delivery. Switching to Cash on Delivery.',
  PRICE_UNAVAILABLE: 'Some items are not currently available for online checkout.',
  COD_NOT_AVAILABLE: 'Cash on Delivery is not available for this order.',
  STOCK_CHANGED: 'Stock changed for one or more items. Please review your cart.',
  DELIVERY_SLOTS_UNAVAILABLE: 'Delivery slots are unavailable between 12:00 AM and 6:00 AM. Please come back after 6:00 AM to place your order.',
  DELIVERY_SLOT_REQUIRED: 'Please select an available delivery slot.',
  INVALID_DELIVERY_SLOT: 'Please select an available delivery slot.',
  NO_DELIVERY_SLOTS_AVAILABLE: 'No delivery slots are currently available. Please try again later.',
  DELIVERY_SLOT_EXPIRED: 'This delivery slot is no longer available. Please choose the next available slot.',
};

// Selectable radio-card shared by the address and payment-method lists, so
// both render an identical box in every state instead of each list
// hand-rolling its own selected/disabled styling.
const optionCardClass = (selected, disabled = false) =>
  `flex items-center gap-3 p-4 rounded-xl border transition-colors ${
    disabled
      ? 'border-[#E6EEE6] bg-[#F8FAF7] opacity-60 cursor-not-allowed'
      : `cursor-pointer ${
          selected ? 'border-[#2E8B47] bg-[#F1FAEF]' : 'border-[#E6EEE6] hover:bg-emerald-50/50'
        }`
  }`;

const Radio = ({ selected }) => (
  <span
    className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
      selected ? 'border-[#2E8B47] bg-[#2E8B47]' : 'border-[#C9D6C9]'
    }`}
    aria-hidden="true"
  >
    {selected && <span className="w-2 h-2 rounded-full bg-white" />}
  </span>
);

// A saved address is identified by its id; the legacy single address the API
// projects without one gets a stable stand-in so it can still be chosen.
const addressKeyOf = (address, index) => address._id || `saved-${index}`;

// Why the address cannot be priced yet, in the customer's words.
const ADDRESS_PROBLEMS = {
  pincode_missing: 'Add a 6-digit pincode to this address to continue.',
  coordinates_missing:
    "This address doesn't have a pinned location yet. Use a new address and pin your location to continue.",
};

// An order the backend queued (PENDING_ALLOCATION) is a real order: it is
// remembered for this tab so a reload cannot lead to placing it a second time.
//
// One record PER CUSTOMER, under a key named by their account id (nothing
// else is stored): customers taking turns in one tab each keep their own
// outstanding order, and no one's record can overwrite or clear another's.
// The unscoped key is from older versions only: a record in it is moved to
// the account it names, and one that names no account is used only once the
// backend — whose lookup is the signed-in customer's own — confirms it.
const PENDING_ORDER_STORAGE_KEY = 'fnh_pending_order';
const pendingKeyFor = (customerId) => `${PENDING_ORDER_STORAGE_KEY}:${customerId}`;
// A stored record, or null. Anything unreadable is removed, so it can never
// hold checkout.
const readRecord = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
    if (parsed?.saleId) return { saleId: String(parsed.saleId), customerId: parsed.customerId ? String(parsed.customerId) : null, checkoutGroupId: parsed.checkoutGroupId ? String(parsed.checkoutGroupId) : null };
    sessionStorage.removeItem(key);
    return null;
  } catch {
    return null;
  }
};
const writeStoredPendingOrder = (customerId, saleId, checkoutGroupId = null) => {
  if (!customerId) return;
  try {
    sessionStorage.setItem(pendingKeyFor(customerId), JSON.stringify({ saleId, ...(checkoutGroupId ? { checkoutGroupId } : {}) }));
    if (readRecord(PENDING_ORDER_STORAGE_KEY)?.saleId === saleId) sessionStorage.removeItem(PENDING_ORDER_STORAGE_KEY);
  } catch {
    // Storage unavailable: the pending state still holds for this page.
  }
};
// Only this customer's record for this order is removed (and the matching
// unscoped one, once the backend has answered for it) — never anyone else's.
const removeStoredPendingOrder = (customerId, saleId) => {
  try {
    if (customerId && readRecord(pendingKeyFor(customerId))?.saleId === saleId) sessionStorage.removeItem(pendingKeyFor(customerId));
    const legacy = readRecord(PENDING_ORDER_STORAGE_KEY);
    if (legacy?.saleId === saleId && (!legacy.customerId || legacy.customerId === customerId)) {
      sessionStorage.removeItem(PENDING_ORDER_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: nothing to remove.
  }
};
// This customer's remembered order, if any. 'verifying' marks an unscoped
// record that does not yet say whose it is.
const pendingOrderFor = (customerId) => {
  if (!customerId) return null;
  const own = readRecord(pendingKeyFor(customerId));
  if (own) return { saleId: own.saleId, checkoutGroupId: own.checkoutGroupId, status: 'pending', message: null };
  const legacy = readRecord(PENDING_ORDER_STORAGE_KEY);
  if (!legacy) return null;
  if (legacy.customerId) {
    // Named: it belongs to that account. Move it there unless that account
    // already has a newer record of its own.
    try {
      if (!readRecord(pendingKeyFor(legacy.customerId))) {
        sessionStorage.setItem(pendingKeyFor(legacy.customerId), JSON.stringify({ saleId: legacy.saleId }));
      }
      sessionStorage.removeItem(PENDING_ORDER_STORAGE_KEY);
    } catch {
      // Storage unavailable: read it again next time.
    }
    return legacy.customerId === customerId ? { saleId: legacy.saleId, status: 'pending', message: null } : null;
  }
  return { saleId: legacy.saleId, status: 'verifying', message: null };
};
// A placement whose outcome is not known yet — its answer was lost, it timed
// out, or it is awaiting payment — saved BEFORE the request leaves the page,
// so that after a reload (or a closed tab) the same order is checked again
// under the same Idempotency-Key with the exact same body: the backend then
// replays the original outcome instead of creating a second order.
//
// One per customer, under their account id. Holds the key, the attempt's
// fingerprint, the exact request body (items, delivery address, slot,
// payment method, coupon, quote token, reviewed total), the sale once known,
// and when it was made. Never an auth token or any payment credential.
// Removed only on a definite outcome — never because time has passed.
const ATTEMPT_STORAGE_PREFIX = 'fnh_order_attempt:';
const ATTEMPT_SCHEMA = 1;
const attemptStorageKey = (customerId) => `${ATTEMPT_STORAGE_PREFIX}${customerId}`;
const readStoredAttempt = (customerId) => {
  if (!customerId) return null;
  try {
    const raw = localStorage.getItem(attemptStorageKey(customerId));
    if (raw === null) return null;
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
    const valid = parsed?.schema === ATTEMPT_SCHEMA
      && parsed.customerId === customerId
      && typeof parsed.key === 'string' && parsed.key
      && typeof parsed.fingerprint === 'string'
      && parsed.payload && typeof parsed.payload === 'object' && Array.isArray(parsed.payload.items);
    if (valid) {
      return {
        key: parsed.key,
        fingerprint: parsed.fingerprint,
        payload: parsed.payload,
        saleId: parsed.saleId ? String(parsed.saleId) : null,
        checkoutGroupId: parsed.checkoutGroupId ? String(parsed.checkoutGroupId) : null,
        createdAt: Number(parsed.createdAt) || null,
      };
    }
    // Unreadable: it cannot be replayed exactly, so it must not hold checkout.
    localStorage.removeItem(attemptStorageKey(customerId));
    return null;
  } catch {
    return null;
  }
};
// Saves the attempt and reads it back. Returns whether it is now really
// stored, exactly — a placement is never sent unless it is.
const writeStoredAttempt = (customerId, attempt) => {
  if (!customerId) return false;
  try {
    const record = {
      schema: ATTEMPT_SCHEMA,
      customerId,
      key: attempt.key,
      fingerprint: attempt.fingerprint,
      payload: attempt.payload,
      saleId: attempt.saleId || null,
      checkoutGroupId: attempt.checkoutGroupId || null,
      createdAt: attempt.createdAt || Date.now(),
    };
    localStorage.setItem(attemptStorageKey(customerId), JSON.stringify(record));
    const back = readStoredAttempt(customerId);
    return Boolean(back) && back.key === record.key && JSON.stringify(back.payload) === JSON.stringify(record.payload);
  } catch {
    return false;
  }
};
// Only this customer's record, and only for this attempt.
const removeStoredAttempt = (customerId, key) => {
  try {
    if (customerId && key && readStoredAttempt(customerId)?.key === key) localStorage.removeItem(attemptStorageKey(customerId));
  } catch {
    // Storage unavailable: nothing to remove.
  }
};
const PENDING_POLL_MS = 20000;
const FINALIZING_MESSAGE = "Your payment was received and we're confirming your order. Please check again in a moment — you won't be charged again.";
// States the backend can still move on its own, so the page keeps asking.
const PENDING_POLLED = new Set(['pending', 'ready', 'expiring', 'finalizing']);
// States the backend has settled: the local record is no longer needed.
const PENDING_SETTLED = new Set(['expired', 'failed', 'refund_pending', 'refunded']);

let razorpayScriptPromise = null;
const loadRazorpayScript = () => {
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load payment gateway.'));
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
};

// The design's step index beside the form. It only describes the sections on
// this single page (which remains one form, not a wizard): the address is
// step 1, and the rest follow once an address is deliverable.
const STEPS = [
  { label: 'Delivery Address', hint: 'Select delivery address' },
  { label: 'Payment Method', hint: 'Choose payment option' },
  { label: 'Delivery Slot', hint: 'Select date & time' },
  { label: 'Review & Place Order', hint: 'Confirm your order' },
];

// Brand promises already used across the site.
const ASSURANCES = [
  { icon: Leaf, title: 'Fresh', subtitle: 'Handpicked products' },
  { icon: PackageCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
  { icon: ShieldCheck, title: 'Quality Checked', subtitle: 'Before dispatch' },
];

// Day/evening glyph from the slot's own start time when the backend gives one.
const slotIcon = (slot) => {
  const source = slot.startTime || slot.label || '';
  const match = /(\d{1,2})(?::\d{2})?\s*(AM|PM)?/i.exec(source);
  if (!match) return null;
  let hour = Number(match[1]);
  if (match[2]) {
    if (/pm/i.test(match[2]) && hour < 12) hour += 12;
    if (/am/i.test(match[2]) && hour === 12) hour = 0;
  }
  return hour >= 18 || hour < 6 ? Moon : Sun;
};

const card = 'bg-white rounded-2xl border border-[#E6EEE6] shadow-[0_6px_18px_-10px_rgba(15,51,24,0.25)]';
const CardHeading = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <h2 className="flex items-center gap-3 serif text-[20px] font-bold text-emerald-950 leading-tight">
      <span className="w-[42px] h-[42px] rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
        <Icon size={20} aria-hidden="true" />
      </span>
      <span>
        {title}
        {subtitle && <span className="block text-[12.5px] font-sans font-normal text-ink-secondary mt-[2px]">{subtitle}</span>}
      </span>
    </h2>
    {action}
  </div>
);

const CheckoutPage = () => {
  const { items, clearCart, removeItem, setQuantity } = useCart();
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const { clearCouponCode } = useCouponIntent();
  const { customer, validatedSession } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newAddress, setNewAddress] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  // The customer's slot choice. Cleared whenever another address is chosen,
  // and only ever honoured for the slot list (geography) it was made from.
  const [slotChoice, setSlotChoice] = useState({ key: null, id: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [placing, setPlacing] = useState(false);
  // What the last placement attempt came back with, when the customer must act
  // before trying again (a changed total, a slot, a payment method, stock).
  const [notice, setNotice] = useState(null);
  const [stockConflict, setStockConflict] = useState(null);
  const [slotNeedsChoice, setSlotNeedsChoice] = useState(null);
  const [codRejection, setCodRejection] = useState(null);
  const [submittedTotal, setSubmittedTotal] = useState(null);
  const customerId = customer?._id ? String(customer._id) : null;
  const [pendingOrder, setPendingOrderState] = useState(() => pendingOrderFor(customerId));
  const pendingRef = useRef(pendingOrder);
  pendingRef.current = pendingOrder;
  const { locating, locationError, captureLocation } = useCurrentLocation();

  // Who an answer is for. Every awaited checkout request captures this before
  // it starts, and its answer is applied only if, when it arrives, this page
  // is still mounted and still the same signed-in account's (the generation
  // moves on every account change, so A → B → A does not revive A's old
  // requests). Anything else — another account, a page that has gone — is
  // dropped without touching the cart, the route or any state.
  const aliveRef = useRef(true);
  const accountRef = useRef({ customerId, generation: 0 });
  if (accountRef.current.customerId !== customerId) {
    accountRef.current = { customerId, generation: accountRef.current.generation + 1 };
  }
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  // Also the LOGIN session: a sign-in elsewhere (another tab, or the same
  // account signing in again) voids every operation the old session began.
  const captureContext = () => ({ account: accountRef.current, session: validatedSession });
  const isCurrent = (context) => aliveRef.current
    && Boolean(context.session)
    && accountRef.current === context.account
    && getCurrentSessionId() === context.session;
  // Status answers also have to be the newest one asked, for the order on screen.
  const statusRequestRef = useRef(0);
  const splitRequestRef = useRef(0);

  const rememberPendingOrder = (order) => {
    pendingRef.current = order;
    setPendingOrderState(order);
    writeStoredPendingOrder(customerId, order.saleId, order.checkoutGroupId);
  };
  const forgetPendingOrder = (saleId) => {
    removeStoredPendingOrder(customerId, saleId);
    if (pendingRef.current?.saleId === saleId) pendingRef.current = null;
    setPendingOrderState((current) => (current?.saleId === saleId ? null : current));
  };

  // One intended order attempt = one exact request body = one Idempotency-Key
  // (the backend refuses a key reused with a different body). The key is kept
  // only while that attempt's outcome is open — an uncertain failure, a
  // payment order awaiting payment, or an order the backend queued — so a
  // retry is recognised as the same order; any definite answer retires it.
  //
  // A queued order's key is tied to its sale. Reusing it would only replay
  // that order's queued answer, so once the backend confirms the order is
  // over without being placed (cancelled, expired, refunded, not found) the
  // key is retired and the customer's next deliberate placement is new.
  // Nothing else retires it: not a failed lookup, a timeout or time passing.
  const attemptRef = useRef(null);
  const placingRef = useRef(false);
  // An attempt whose outcome is unknown (see ATTEMPT_STORAGE_PREFIX): until it
  // is checked, no other order can be placed, and nothing on the page — a new
  // address, slot or payment method — can replace its key.
  const [unresolvedAttempt, setUnresolvedAttemptState] = useState(null);
  const unresolvedRef = useRef(null);
  const setUnresolvedAttempt = (attempt) => {
    unresolvedRef.current = attempt;
    setUnresolvedAttemptState(attempt);
  };
  const resetIdempotencyKey = () => {
    if (!unresolvedRef.current && !pendingRef.current) attemptRef.current = null;
  };
  // After a "not now" answer (429 …) the check waits before it may ask again.
  const [recoveryHeld, setRecoveryHeld] = useState(false);
  const holdRecovery = (err) => {
    setRecoveryHeld(true);
    setTimeout(() => setRecoveryHeld(false), retryDelayMs(err));
  };
  // The current customer's unresolved attempt, as stored: it blocks every
  // new placement until the backend has settled it.
  const blockOnStoredAttempt = () => {
    const stored = readStoredAttempt(customerId);
    if (stored) setUnresolvedAttempt(stored);
    return stored;
  };
  // A definite outcome for the current attempt: it is over, here and stored.
  const settleAttempt = () => {
    const key = attemptRef.current?.key || unresolvedRef.current?.key;
    removeStoredAttempt(customerId, key);
    attemptRef.current = null;
    setUnresolvedAttempt(null);
  };
  const retireAttemptFor = (saleId) => {
    if (attemptRef.current?.saleId === saleId) settleAttempt();
  };
  const keyForAttempt = (fingerprint) => {
    if (attemptRef.current?.fingerprint !== fingerprint) {
      attemptRef.current = { fingerprint, key: crypto.randomUUID() };
    }
    return attemptRef.current.key;
  };
  const endPlacing = () => {
    placingRef.current = false;
    setPlacing(false);
  };
  // Each placing operation's id: one that was voided (its login session
  // ended) only frees the page's own busy state, and only if nothing newer
  // has started since — it touches no cart, order or attempt.
  const placingOpRef = useRef(0);
  const beginPlacing = () => {
    placingRef.current = true;
    setPlacing(true);
    placingOpRef.current += 1;
    return placingOpRef.current;
  };
  const abandon = (op) => {
    if (aliveRef.current && placingOpRef.current === op) endPlacing();
  };

  usePageMetadata({
    title: 'Checkout | Fresh & Homely',
    description: 'Complete your Fresh & Homely order.',
    canonicalPath: '/checkout',
  });

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart', { replace: true });
      return;
    }
    // Delivery slots are no longer loaded here: they belong to the outlet
    // serving the chosen delivery address (see useCheckoutQuote).
    Promise.all([addressesApi.listAddresses(), configApi.getPaymentSettings()])
      .then(([addressRes, paymentRes]) => {
        const list = addressRes.data || [];
        setAddresses(list);
        const defaultIndex = Math.max(0, list.findIndex((a) => a.isDefault));
        if (list[defaultIndex]) setSelectedAddressId(addressKeyOf(list[defaultIndex], defaultIndex));
        setPaymentSettings(paymentRes.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const selectedAddress = addresses?.find((a, index) => addressKeyOf(a, index) === selectedAddressId);

  // Checkout's geography is the delivery address being used — a saved one, or
  // the new one being typed — never the browsing location.
  const deliveryTarget = selectedAddressId ? selectedAddress || null : newAddress;
  const checkout = useCheckoutQuote(deliveryTarget);
  const { quote } = checkout;

  // Slots for THIS address only. A choice made for another address's list is
  // not carried over; the first available slot is offered instead.
  const slotList = checkout.slots?.orderingAvailable ? checkout.slots.slots || [] : [];
  const chosenSlot =
    slotChoice.key === checkout.slotsKey ? slotList.find((slot) => slot.id === slotChoice.id && slot.available) : null;
  // After the backend refused the chosen slot, a new one must be picked by the
  // customer — none is chosen for them.
  const mustChooseSlot = Boolean(slotNeedsChoice) && slotNeedsChoice.key === checkout.slotsKey;
  const selectedSlotId = (chosenSlot || (mustChooseSlot ? null : slotList.find((slot) => slot.available)))?.id || null;

  // Payment: Cash on Delivery only when the published payment settings allow
  // it for this address and this quote's total, and the backend has not
  // refused it; weight-variable items can only be paid on delivery.
  const hasCatchWeightItem = useMemo(() => items.some((item) => item.product?.catchWeight), [items]);
  const codRefusal =
    codRejection && (codRejection.geographyKey === checkout.geographyKey || ['disabled_globally', 'disabled_for_customer'].includes(codRejection.reason))
      ? codRejection.reason
      : null;
  const codReason = codRestriction(paymentSettings, { pincode: deliveryTarget?.pincode, grandTotal: quote?.grandTotal }) || codRefusal;
  const codAvailable = !codReason;
  const requestedMethod = hasCatchWeightItem ? 'cod' : paymentMethod;
  const effectivePaymentMethod = requestedMethod === 'cod' && !codAvailable ? null : requestedMethod;
  const stockBlocked = Boolean(stockConflict) && stockConflict.quoteKey === checkout.quoteKey;

  // Checkout proceeds only on a fresh quote for this address, cart and coupon.
  const quoteIssues = quote?.issues || [];
  const issueFor = (productId) => quoteIssues.find((issue) => String(issue.productId) === String(productId));
  const quoteLineFor = (productId) => quote?.items?.find((line) => String(line.productId) === String(productId));
  const lineProblems = quoteIssues.filter((issue) => issue.productId);
  const unpricedLines = quote ? items.filter((item) => !quoteLineFor(item.productId) && !issueFor(item.productId)) : [];
  const basketIssues = quoteIssues.filter((issue) => !issue.productId && issue.type !== 'coupon_invalid');
  const blockedByLocation = quoteIssues.some((issue) => issue.type === 'location_required');

  let checkoutBlocker = null;
  if (pendingOrder) checkoutBlocker = 'Your order has been received and is being arranged.';
  else if (unresolvedAttempt) checkoutBlocker = 'Check your last order before placing a new one.';
  else if (!deliveryTarget) checkoutBlocker = 'Choose a delivery address.';
  else if (ADDRESS_PROBLEMS[checkout.serviceStatus]) checkoutBlocker = ADDRESS_PROBLEMS[checkout.serviceStatus];
  else if (checkout.serviceStatus === 'resolving') checkoutBlocker = 'Checking delivery to this address…';
  else if (checkout.serviceStatus === 'error') checkoutBlocker = "We couldn't check delivery to this address.";
  else if (checkout.serviceStatus === 'unserviceable') checkoutBlocker = "We don't deliver to this address yet.";
  else if (checkout.quoteStatus === 'error') checkoutBlocker = "We couldn't confirm your order total.";
  else if (!quote) checkoutBlocker = 'Confirming your order total…';
  else if (blockedByLocation) checkoutBlocker = "We can't deliver this order to this address.";
  else if (lineProblems.length > 0 || unpricedLines.length > 0)
    checkoutBlocker = 'Some items are unavailable for this address. Update your cart to continue.';
  else if (checkout.slotsStatus === 'loading') checkoutBlocker = 'Loading delivery times…';
  else if (checkout.slotsStatus === 'error' || !checkout.slots?.orderingAvailable)
    checkoutBlocker = 'No delivery time is available for this address right now.';
  else if (stockBlocked) checkoutBlocker = 'Update the quantities in your cart to continue.';
  else if (!selectedSlotId) checkoutBlocker = 'Choose a delivery slot.';
  else if (!effectivePaymentMethod)
    checkoutBlocker = hasCatchWeightItem
      ? "This order has a weight-variable item that must be paid on delivery, but Cash on Delivery isn't available."
      : 'Choose a payment method.';
  else if (checkout.expectedGrandTotal === null) checkoutBlocker = 'Confirming your order total…';
  else if (!selectedAddressId && !newAddress?.line1) checkoutBlocker = 'Complete the delivery address.';
  const checkoutReady = !checkoutBlocker;

  const couponView = checkout.couponCode && quote ? couponResult(quote, checkout.couponCode) : null;

  const handleAddressField = (field) => (event) => {
    setNewAddress((prev) => ({ ...(prev || {}), [field]: event.target.value }));
    resetIdempotencyKey();
  };

  const handlePinLocation = async () => {
    const coordinates = await captureLocation();
    if (coordinates) {
      setNewAddress((prev) => ({ ...(prev || {}), coordinates }));
      resetIdempotencyKey();
    }
  };

  const buildDeliveryAddress = () => {
    if (selectedAddressId && selectedAddress) {
      return {
        addressId: selectedAddress._id,
        line1: selectedAddress.line1,
        line2: selectedAddress.line2 || '',
        city: selectedAddress.city,
        state: selectedAddress.state,
        pincode: selectedAddress.pincode,
        landmark: selectedAddress.landmark || '',
        coordinates: selectedAddress.coordinates,
      };
    }
    return newAddress;
  };

  // The order is done only when the backend says so: COD / wallet finalized,
  // or an online payment the backend verified. Only then is anything cleared.
  const completeOrder = (saleId) => {
    settleAttempt();
    forgetPendingOrder(saleId);
    clearCouponCode();
    clearCart();
    navigate(`/order-confirmation/${saleId}`);
  };

  const applySplitCartReconciliation = (groupId, sales) => {
    if (groupId && isGroupReconciled(groupId)) {
      return;
    }
    const result = reconcileCart(itemsRef.current, sales);
    const remainSet = new Map(result.remaining.map((r) => [r.productId, r.quantity]));
    for (const cartItem of itemsRef.current) {
      const pid = String(cartItem.productId);
      if (remainSet.has(pid)) {
        setQuantity(pid, remainSet.get(pid));
      } else {
        removeItem(pid);
      }
    }
    if (groupId) markGroupReconciled(groupId);
  };

  const completeSplitOrder = (groupId, sales) => {
    const selected = sales.find(isCommittedSale) || sales[0];
    applySplitCartReconciliation(groupId, sales);
    forgetPendingOrder(pendingRef.current?.saleId || attemptRef.current?.saleId);
    settleAttempt();
    clearCouponCode();
    navigate(`/order-confirmation/${selected.saleId}?group=${encodeURIComponent(groupId)}`);
  };

  const rememberSplitGroup = (groupId, saleId) => {
    if (attemptRef.current) {
      attemptRef.current = { ...attemptRef.current, saleId, checkoutGroupId: groupId };
      const saved = readStoredAttempt(customerId);
      if (saved?.key === attemptRef.current.key) writeStoredAttempt(customerId, { ...saved, saleId, checkoutGroupId: groupId });
    }
  };

  // All split completion, polling and recovery use the same authoritative
  // group view. A captured payment or one invoiced child does not settle it.
  const checkSplitGroup = async (groupId, context = captureContext(), stillWanted = () => true) => {
    const request = ++splitRequestRef.current;
    const current = () => isCurrent(context) && request === splitRequestRef.current && stillWanted();
    if (!current()) return null;
    try {
      let { data } = await ordersApi.getCheckoutGroup(groupId, { session: context.session });
      if (!current()) return null;
      if (splitGroupOutcome(data) === 'pending' && data?.razorpayPaymentId) {
        // This endpoint retries interrupted capture/finalization safely. The
        // summary supplies the authoritative quantities after that recovery.
        await ordersApi.getCheckoutGroupPaymentStatus(groupId, { session: context.session });
        if (!current()) return null;
        ({ data } = await ordersApi.getCheckoutGroup(groupId, { session: context.session }));
        if (!current()) return null;
      }
      const sales = data?.sales || [];
      const outcome = splitGroupOutcome(data);
      if (outcome === 'settled') {
        // Older backend projections do not include quantities. Keep recovery
        // protected rather than clearing or re-offering an unknown basket.
        if (sales.some(sale => isCommittedSale(sale) && (!Array.isArray(sale.items) || !sale.items.length))) {
          throw new Error('Order item details are unavailable');
        }
        completeSplitOrder(groupId, sales);
        return 'settled';
      }
      const saleId = attemptRef.current?.saleId || pendingRef.current?.saleId || sales[0]?.saleId;
      if (!saleId) throw new Error('Order details are unavailable');
      rememberSplitGroup(groupId, String(saleId));
      rememberPendingOrder({ saleId: String(saleId), checkoutGroupId: groupId, paymentMethod: data?.paymentMethod, status: outcome === 'ready' ? 'ready' : 'finalizing' });
      setUnresolvedAttempt(null);
      setError(null);
      return outcome;
    } catch (err) {
      if (!current()) return null;
      const stored = blockOnStoredAttempt();
      if (err?.status === 404 && stored?.saleId) {
        rememberPendingOrder({ saleId: stored.saleId, checkoutGroupId: groupId, status: 'missing' });
      }
      setError("We couldn't check your orders just now. Please check again before placing another order.");
      // Cleanup can remove an abandoned group. Only placement-key recovery
      // can prove whether the original attempt is safe to replay or retire.
      return err?.status === 404 ? 'missing' : null;
    }
  };

  // Razorpay opens only for a payment order the backend created, with the
  // backend's own amount and order id — never an amount from this page.
  const openPayment = async (data) => {
    const context = captureContext();
    if (!isCurrent(context)) return;
    await loadRazorpayScript();
    if (!isCurrent(context)) return;
    const rzp = new window.Razorpay({
      key: data.razorpayKeyId,
      order_id: data.razorpayOrderId,
      amount: data.amount,
      currency: data.currency,
      name: 'Fresh & Homely',
      prefill: { name: customer?.name, email: customer?.email, contact: customer?.phone },
      handler: async (paymentResponse) => {
        if (!isCurrent(context)) return;
        if (data.checkoutGroupId) {
          try {
            await ordersApi.confirmCheckoutGroupPayment(data.checkoutGroupId, paymentResponse.razorpay_payment_id, paymentResponse.razorpay_signature, { session: context.session });
          } catch {
            // A lost confirmation is not a failed payment. Resolve the group.
          }
          if (!isCurrent(context)) return;
          await checkSplitGroup(data.checkoutGroupId, context);
          if (isCurrent(context)) endPlacing();
          return;
        }

        try {
          const confirmRes = await ordersApi.confirmPayment(
            data.saleId,
            paymentResponse.razorpay_payment_id,
            paymentResponse.razorpay_signature,
            { session: context.session }
          );
          if (!isCurrent(context)) return;
          if (isConfirmedPayment(confirmRes.data)) {
            completeOrder(data.saleId);
            return;
          }
          const refundStatus = confirmRes.data?.status;
          if (REFUND_MESSAGES[refundStatus]) {
            // The order was not placed. Only 'refunded' means the money is
            // back; 'refund_pending' is a refund still being processed.
            settleAttempt();
            endPlacing();
            if (pendingRef.current?.saleId === data.saleId) {
              settlePendingOrder(data.saleId, refundStatus);
            } else {
              setNotice({ kind: 'info', message: REFUND_MESSAGES[refundStatus] });
            }
            showToast(refundStatus === 'refunded' ? 'Order not placed — payment refunded' : 'Order not placed — refund in progress', {
              tone: 'error',
            });
            return;
          }
          throw new Error('Payment not confirmed');
        } catch (confirmErr) {
          if (!isCurrent(context)) return;
          // The callback can occasionally fail to register a successful charge;
          // ask the backend once before surfacing an error to the user.
          try {
            const statusRes = await ordersApi.getPaymentStatus(data.saleId, { session: context.session });
            if (!isCurrent(context)) return;
            const outcome = pendingOrderOutcome(statusRes.data);
            if (outcome === 'complete') {
              completeOrder(data.saleId);
              return;
            }
            if (outcome === 'finalizing') {
              // Paid, the order not finished yet: never a false success.
              blockOnStoredAttempt();
              setNotice({ kind: 'info', message: FINALIZING_MESSAGE });
              endPlacing();
              return;
            }
          } catch {
            // fall through to error display
          }
          if (!isCurrent(context)) return;
          // Neither the confirmation nor the status gave an answer: the
          // payment may have gone through. The order stays this customer's
          // unresolved attempt — nothing on the page can replace it — until
          // the backend says where it stands.
          blockOnStoredAttempt();
          setError('Payment confirmation failed. Please check your order history before retrying.');
          showToast('Payment confirmation failed', { tone: 'error' });
          endPlacing();
        }
      },
      modal: {
        // Nothing is cleared: the same attempt (same key) replays this payment
        // order if the customer tries again, instead of creating another.
        ondismiss: () => {
          if (!isCurrent(context)) return;
          // The order exists and a payment may still complete: continue THIS
          // order (after a status check) — no other order meanwhile.
          blockOnStoredAttempt();
          setNotice({ kind: 'info', message: "Your payment wasn't completed. You can try again when you're ready." });
          endPlacing();
        },
      },
    });
    rzp.open();
  };

  // Before (re)opening payment for an order this page already knew about —
  // a recovered attempt, or a retry after the payment window was closed —
  // ask the backend where that order stands. Payment opens only for an order
  // that is still open and unpaid.
  const paymentStillOpen = async (saleId) => {
    const context = captureContext();
    if (!isCurrent(context)) return false;
    const groupId = attemptRef.current?.checkoutGroupId || pendingRef.current?.checkoutGroupId;
    if (groupId) return (await checkSplitGroup(groupId, context)) === 'ready';
    let data;
    try {
      data = (await ordersApi.getPaymentStatus(saleId, { session: context.session }))?.data;
    } catch {
      if (!isCurrent(context)) return false;
      blockOnStoredAttempt();
      setError("We couldn't check your order's payment just now. Please try again.");
      return false;
    }
    if (!isCurrent(context)) return false;
    const outcome = pendingOrderOutcome(data);
    if (outcome === 'complete') {
      completeOrder(saleId);
      return false;
    }
    if (outcome === 'finalizing') {
      // Paid, but the order is not finished: no payment again, no success yet.
      blockOnStoredAttempt();
      setNotice({ kind: 'info', message: FINALIZING_MESSAGE });
      return false;
    }
    if (PENDING_SETTLED.has(outcome)) {
      settleAttempt();
      setNotice({
        kind: 'info',
        message: REFUND_MESSAGES[outcome] || "Your last order wasn't completed, so it wasn't placed. You can place a new order.",
      });
      return false;
    }
    if (outcome === 'ready') return true;
    blockOnStoredAttempt();
    setError("We couldn't confirm where your last order stands. Please try again.");
    return false;
  };

  // A successful (2xx) answer from placement or from resuming a queued order.
  const handleCheckoutResponse = async (response, { recovered = false } = {}) => {
    const { data } = response || {};
    if (response?.code === 'PENDING_ALLOCATION') {
      if (attemptRef.current) attemptRef.current.saleId = String(data.saleId);
      removeStoredAttempt(customerId, attemptRef.current?.key);
      setUnresolvedAttempt(null);
      rememberPendingOrder({ saleId: String(data.saleId), status: 'pending', message: response.message || null });
      endPlacing();
      return;
    }
    if (data?.fulfillmentMode === 'split') {
      const context = captureContext();
      if (!isCurrent(context)) return;
      const groupId = data.checkoutGroupId;
      const saleId = data.sales?.[0]?.saleId;
      if (!groupId || !saleId) {
        blockOnStoredAttempt();
        setError('We could not check your orders. Please check again.');
        endPlacing();
        return;
      }
      rememberSplitGroup(String(groupId), String(saleId));
      const outcome = await checkSplitGroup(String(groupId), context);
      if (!isCurrent(context)) return;
      if (outcome === 'ready' && data.paymentMethod === 'online' && data.razorpayOrderId) {
        await openPayment({ ...data, saleId: String(saleId), checkoutGroupId: String(groupId) });
      } else {
        endPlacing();
      }
      return;
    }
    if (data?.paymentMethod === 'cod' || data?.paymentMethod === 'wallet') {
      completeOrder(data.saleId);
      return;
    }
    if (data?.paymentMethod === 'online' && data.razorpayOrderId) {
      const saleId = String(data.saleId);
      const known = attemptRef.current?.saleId === saleId;
      if (attemptRef.current) {
        attemptRef.current.saleId = saleId;
        const saved = readStoredAttempt(customerId);
        if (saved?.key === attemptRef.current.key) writeStoredAttempt(customerId, { ...saved, saleId });
      }
      if ((recovered || known) && !(await paymentStillOpen(saleId))) {
        endPlacing();
        return;
      }
      setUnresolvedAttempt(null);
      await openPayment(data);
      return;
    }
    settleAttempt();
    setError('We could not start the payment for this order. Please try again.');
    endPlacing();
  };

  // A refused (or unanswered) placement. Nothing is charged, nothing cleared.
  const handlePlacementError = (err, snapshot) => {
    endPlacing();
    if (err?.code === 'ORDER_ALREADY_PLACED' && err.details?.data?.saleId) {
      // The key already made an order: that order is the outcome.
      const attempt = unresolvedRef.current || readStoredAttempt(customerId);
      if (attempt) {
        reconcileSale(attempt, err.details.data);
        return;
      }
    }
    if (isUncertainOutcome(err)) {
      // No proof that nothing was placed (no answer, 5xx, 429, …): the same
      // attempt stays — same key, same body — and until the backend settles
      // it nothing else can be placed.
      setUnresolvedAttempt(readStoredAttempt(customerId) || unresolvedRef.current);
      if (err?.status === 429 || err?.retryAfterSeconds != null) holdRecovery(err);
      setError(
        err.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS'
          ? 'Your order is still being processed. Please wait a moment, then try again.'
          : "We couldn't confirm whether your order went through. Please try again — the same order won't be placed twice."
      );
      return;
    }
    // A definite refusal: no order was made, and the backend released the key.
    settleAttempt();
    const body = err.details || {};
    if (err.code === 'QUOTE_CHANGED') {
      // The backend's fresh quote becomes Checkout's quote only if it was
      // priced for exactly what is still on screen; otherwise ask again.
      const installed = Boolean(body.quote) && checkout.replaceQuote(snapshot.quoteKey, body.quote);
      if (!installed) checkout.retryQuote();
      setNotice({
        kind: 'quote_changed',
        reason: quoteChangeReason(body.reason, body.message),
        previousTotal: snapshot.expectedGrandTotal,
        newTotal: installed && typeof body.quote.grandTotal === 'number' ? body.quote.grandTotal : null,
      });
      return;
    }
    if (err.code === 'STOCK_CHANGED') {
      setStockConflict({ quoteKey: snapshot.quoteKey, items: Array.isArray(body.items) ? body.items : [] });
      checkout.retryQuote();
      return;
    }
    if (SLOT_CONFLICT_CODES.has(err.code)) {
      setSlotChoice({ key: null, id: null });
      setSlotNeedsChoice({ key: checkout.slotsKey });
      checkout.retrySlots();
      checkout.retryQuote();
      setNotice({
        kind: 'slot',
        message: `${ERROR_MESSAGES[err.code] || 'That delivery slot is no longer available.'} Please choose a delivery slot again.`,
      });
      return;
    }
    if (err.code === 'COD_NOT_AVAILABLE') {
      setCodRejection({ reason: body.reason || null, geographyKey: checkout.geographyKey });
      setNotice({ kind: 'payment', message: `${codUnavailableMessage(body.reason)} Please choose another payment method.` });
      return;
    }
    if (err.code === 'CATCH_WEIGHT_REQUIRES_COD') setPaymentMethod('cod');
    if (err.code === 'PINCODE_UNSERVICEABLE') checkout.retryService();
    if (err.code === 'PRICE_UNAVAILABLE' || err.code === 'PRODUCT_NOT_FOUND') checkout.retryQuote();
    setError(ERROR_MESSAGES[err.code] || err.message);
  };

  // Place Order: send exactly the checkout state on screen — the address it
  // was quoted for, its slot, the coupon intent, the quote's token (if any)
  // and total — captured once, so nothing that changes mid-request can mix in.
  const handlePlaceOrder = async () => {
    if (!checkoutReady || placingRef.current || unresolvedRef.current) return;
    const context = captureContext();
    if (!isCurrent(context)) return;
    // Any stored attempt of this customer's (from another tab, or a payment
    // still open) must be settled first.
    if (blockOnStoredAttempt()) return;
    const snapshot = {
      quoteKey: checkout.quoteKey,
      expectedGrandTotal: checkout.expectedGrandTotal,
      payload: buildOrderPayload({
        items,
        deliveryAddress: buildDeliveryAddress(),
        paymentMethod: effectivePaymentMethod,
        deliverySlotId: selectedSlotId,
        couponCode: checkout.couponCode,
        quoteToken: checkout.quoteToken,
        expectedGrandTotal: checkout.expectedGrandTotal,
      }),
    };
    const fingerprint = attemptFingerprint(snapshot.quoteKey, snapshot.payload);
    const idempotencyKey = keyForAttempt(fingerprint);
    const op = beginPlacing();
    setError(null);
    setNotice(null);
    setSubmittedTotal(snapshot.expectedGrandTotal);
    // Saved — and read back — BEFORE the request leaves: if its answer is
    // lost, a reload checks this exact request instead of making a new one.
    // If it cannot be saved, it is not sent at all.
    const saved = writeStoredAttempt(customerId, {
      key: idempotencyKey,
      fingerprint,
      payload: snapshot.payload,
      saleId: attemptRef.current.saleId || null,
      createdAt: Date.now(),
    });
    if (!saved) {
      removeStoredAttempt(customerId, idempotencyKey);
      attemptRef.current = null;
      endPlacing();
      setError("We couldn't save your order safely in this browser, so it wasn't placed. Please allow this site to store data (or leave private browsing), then try again.");
      return;
    }
    try {
      const response = await ordersApi.placeOrder(snapshot.payload, idempotencyKey, { session: context.session });
      if (!isCurrent(context)) return abandon(op);
      await handleCheckoutResponse(response);
    } catch (err) {
      if (!isCurrent(context)) return abandon(op);
      handlePlacementError(err, snapshot);
    }
  };

  // An order exists for the attempt but can no longer be replayed: act on the
  // order itself (never by placing again).
  const reconcileSale = async (attempt, sale) => {
    const outcome = pendingOrderOutcome(sale);
    if (sale.checkoutGroup) {
      const context = captureContext();
      if (!isCurrent(context)) return;
      rememberSplitGroup(String(sale.checkoutGroup), String(sale.saleId));
      await checkSplitGroup(String(sale.checkoutGroup), context);
      if (isCurrent(context)) endPlacing();
      return;
    }
    if (outcome === 'complete') {
      completeOrder(sale.saleId);
      return;
    }
    if (outcome === 'pending') {
      removeStoredAttempt(customerId, attempt.key);
      setUnresolvedAttempt(null);
      attemptRef.current = { fingerprint: attempt.fingerprint, key: attempt.key, saleId: sale.saleId };
      rememberPendingOrder({ saleId: sale.saleId, status: 'pending', message: null });
      endPlacing();
      return;
    }
    if (PENDING_SETTLED.has(outcome)) {
      settleAttempt();
      setNotice({
        kind: 'info',
        message: REFUND_MESSAGES[outcome] || "Your last order wasn't completed, so it wasn't placed. You can place a new order.",
      });
      endPlacing();
      return;
    }
    writeStoredAttempt(customerId, { ...attempt, saleId: sale.saleId });
    setUnresolvedAttempt(readStoredAttempt(customerId) || attempt);
    if (outcome === 'finalizing') setNotice({ kind: 'info', message: FINALIZING_MESSAGE });
    else setError('Your last order is still open. Please check again in a few minutes — no new order can be placed until it is settled.');
    endPlacing();
  };

  // Check the unresolved attempt. The backend is asked first (read only) what
  // its key made; the placement is re-sent — SAME key, SAME body — only while
  // the backend can still replay it, or when nothing was ever made for it
  // within the replay window. Otherwise the existing order is used, the
  // attempt is retired (provably nothing was made), or it stays blocked.
  const handleRecoverAttempt = async () => {
    let attempt = unresolvedRef.current;
    if (!attempt || placingRef.current || recoveryHeld) return;
    const context = captureContext();
    if (!isCurrent(context)) return;
    attemptRef.current = { ...attempt };
    const op = beginPlacing();
    setError(null);
    setNotice(null);
    setSubmittedTotal(attempt.payload.expectedGrandTotal ?? null);
    let groupMissing = false;
    if (attempt.checkoutGroupId) {
      const outcome = await checkSplitGroup(attempt.checkoutGroupId, context);
      if (!isCurrent(context)) return abandon(op);
      if (outcome !== 'missing') { endPlacing(); return; }
      groupMissing = true;
    }
    // The whole recovery belongs to this login session: checked after every
    // await, and each request is bound to it (never sent under another).
    let info;
    try {
      info = (await ordersApi.getPlacementAttempt(attempt.key, {
        madeAt: attempt.createdAt, clientNow: Date.now(), session: context.session,
      }))?.data;
    } catch (err) {
      if (!isCurrent(context)) return abandon(op);
      endPlacing();
      if (isUncertainOutcome(err)) holdRecovery(err);
      setError("We couldn't check your last order just now. Please try again in a moment.");
      return;
    }
    if (!isCurrent(context)) return abandon(op);
    const decision = recoveryDecision(attempt, info, Date.now());
    if (groupMissing && !info?.sale && ['retire', 'replay'].includes(decision.action)) {
      forgetPendingOrder(attempt.saleId);
      attempt = { ...attempt, saleId: null, checkoutGroupId: null };
      attemptRef.current = attempt;
      writeStoredAttempt(customerId, attempt);
      setUnresolvedAttempt(attempt);
      setError(null);
    }
    if (decision.action === 'sale') {
      await reconcileSale(attempt, decision.sale);
      return;
    }
    if (decision.action === 'retire') {
      settleAttempt();
      setNotice({ kind: 'info', message: "Your last order wasn't placed. You can place it again." });
      endPlacing();
      return;
    }
    if (decision.action !== 'replay') {
      endPlacing();
      setError(decision.action === 'wait'
        ? 'Your last order is still being processed. Please check again in a moment.'
        : "We can't confirm yet whether your last order went through, so a new order can't be placed. Please check again later or contact support.");
      return;
    }
    try {
      if (!isCurrent(context)) return abandon(op);
      const response = await ordersApi.placeOrder(attempt.payload, attempt.key, { session: context.session });
      if (!isCurrent(context)) return abandon(op);
      await handleCheckoutResponse(response, { recovered: true });
    } catch (err) {
      if (!isCurrent(context)) return abandon(op);
      // A later profile/quote refusal cannot erase an order already found.
      if (info?.sale && !isUncertainOutcome(err)) {
        await reconcileSale(attempt, info.sale);
        return;
      }
      handlePlacementError(err, { quoteKey: null, expectedGrandTotal: attempt.payload.expectedGrandTotal ?? null });
    }
  };

  // The backend settled a remembered order without placing it: show why, and
  // drop the record — the order itself stays in the customer's history.
  const settlePendingOrder = (saleId, status) => {
    removeStoredPendingOrder(customerId, saleId);
    retireAttemptFor(saleId);
    setPendingOrderState((current) => (current?.saleId === saleId ? { ...current, status } : current));
  };

  // A remembered order: ask the backend (read-only) where it stands. The
  // lookup is the signed-in customer's own, so it also settles whose it is.
  const checkPendingOrder = async (order = pendingRef.current) => {
    if (!order) return;
    const context = captureContext();
    if (!isCurrent(context)) return;
    const request = ++statusRequestRef.current;
    // Applied only for this account, this order, and the newest question.
    const stillWanted = () => isCurrent(context)
      && request === statusRequestRef.current
      && pendingRef.current?.saleId === order.saleId;
    if (order.checkoutGroupId) {
      await checkSplitGroup(order.checkoutGroupId, context, stillWanted);
      return;
    }
    let data;
    try {
      data = (await ordersApi.getPaymentStatus(order.saleId, { session: context.session }))?.data;
    } catch (err) {
      if (!stillWanted()) return;
      if (err?.status === 404) {
        // Not this customer's order (or no longer one): the record is stale
        // and must not hold checkout, and its attempt is over.
        retireAttemptFor(order.saleId);
        forgetPendingOrder(order.saleId);
      } else if (order.status === 'verifying') {
        // A record that does not say whose it is never blocks checkout
        // unconfirmed; it stays stored and is checked again next time.
        setPendingOrderState((current) => (current?.saleId === order.saleId && current.status === 'verifying' ? null : current));
      }
      // Otherwise keep waiting; the next check (or the customer's) asks again.
      return;
    }
    if (!stillWanted()) return;
    const outcome = pendingOrderOutcome(data);
    if (outcome === 'complete') {
      completeOrder(order.saleId);
      return;
    }
    if (!outcome) return;
    if (PENDING_SETTLED.has(outcome)) {
      settlePendingOrder(order.saleId, outcome);
      return;
    }
    // Confirmed as this customer's, and still open.
    writeStoredPendingOrder(customerId, order.saleId);
    setPendingOrderState((current) => {
      if (current?.saleId !== order.saleId) return current;
      // The backend already refused payment as out of time; a draft is only
      // waiting for the backend to release it (or to finish a payment).
      if (outcome === 'ready' && current.status === 'expiring') return current;
      return current.status === outcome ? current : { ...current, status: outcome };
    });
  };

  // The remembered order is the signed-in customer's only. Picked up on
  // arrival and whenever the account changes, and asked about at once.
  useEffect(() => {
    // A different account starts clean: nothing in flight or retried for the
    // previous one carries over (its answers are already dropped above). Its
    // own unresolved attempt, if any, is picked up instead — never another's.
    const attempt = readStoredAttempt(customerId);
    attemptRef.current = attempt ? { ...attempt } : null;
    setUnresolvedAttempt(attempt);
    placingRef.current = false;
    setPlacing(false);
    const order = pendingOrderFor(customerId);
    if (attempt?.saleId && !order) {
      // A payment order was made for it: its outcome can be read without
      // replaying anything.
      paymentStillOpen(attempt.saleId).then((open) => {
        if (open) setNotice({ kind: 'info', message: 'Your last order is waiting for payment.' });
      });
    }
    pendingRef.current = order;
    setPendingOrderState(order);
    if (order) checkPendingOrder(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    if (!pendingOrder || !PENDING_POLLED.has(pendingOrder.status)) return undefined;
    const timer = setInterval(() => checkPendingOrder(), PENDING_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOrder?.saleId, pendingOrder?.status]);

  // A store took the queued order: the customer continues it explicitly.
  const handleContinuePending = async () => {
    const order = pendingRef.current;
    if (!order || placingRef.current) return;
    const context = captureContext();
    if (!isCurrent(context)) return;
    placingRef.current = true;
    setPlacing(true);
    setError(null);
    const stillWanted = () => isCurrent(context) && pendingRef.current?.saleId === order.saleId;
    try {
      if (order.checkoutGroupId) {
        const outcome = await checkSplitGroup(order.checkoutGroupId, context, stillWanted);
        if (!isCurrent(context)) return;
        if (outcome !== 'ready') { endPlacing(); return; }
      }
      const response = order.checkoutGroupId
        ? await ordersApi.resumeCheckoutGroup(order.checkoutGroupId, { session: context.session })
        : await ordersApi.resumeCheckout(order.saleId, { session: context.session });
      if (!stillWanted()) return;
      await handleCheckoutResponse(response);
    } catch (err) {
      if (!stillWanted()) return;
      endPlacing();
      if (order.checkoutGroupId) {
        await checkSplitGroup(order.checkoutGroupId, context, stillWanted);
        return;
      }
      if (err.status === 404) {
        retireAttemptFor(order.saleId);
        forgetPendingOrder(order.saleId);
        return;
      }
      if (err.code === 'CHECKOUT_EXPIRED') {
        // The backend says the time to pay has run out, so Continue cannot
        // succeed again. Checkout reopens once the backend has released the
        // order — not before, in case a payment for it is still landing.
        const expiring = { ...order, status: 'expiring' };
        setPendingOrderState((current) => (current?.saleId === order.saleId ? { ...current, status: 'expiring' } : current));
        await checkPendingOrder(expiring);
        return;
      }
      if (err.code === 'CHECKOUT_UNAVAILABLE') await checkPendingOrder(order);
      setError(ERROR_MESSAGES[err.code] || err.message);
    }
  };

  // Which of the design's steps is "current" — purely a reflection of the
  // state above, never a gate on it.
  const stepReached = !deliveryTarget || checkout.serviceStatus !== 'serviceable' ? 1 : !quote ? 2 : !selectedSlotId ? 3 : 4;

  if (loading) {
    return (
      <>
        <Navbar />
        <section className="pt-24 pb-24 min-h-[60vh] bg-gradient-to-b from-white to-canvas">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SkeletonBlock className="h-10 w-48 mb-10" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6 mb-6 space-y-3">
                <SkeletonBlock className="h-6 w-40" />
                <SkeletonBlock className="h-16 w-full" />
              </div>
            ))}
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="relative overflow-hidden pt-20 pb-6 bg-[linear-gradient(180deg,#F7FBF5_0%,#FFFFFF_30%,#F7FBF5_100%)]">
        <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
          <img src="/pages/checkout-basket.webp" alt="" loading="lazy" width="338" height="438" className="hidden xl:block absolute left-0 top-[432px] w-[338px]" />
          <DecorLeaf className="hidden xl:block absolute left-[40px] top-[110px] w-9 text-emerald-500/70 rotate-[20deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[40px] top-[100px] w-10 text-emerald-500/70 -rotate-[16deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[60px] top-[560px] w-8 text-emerald-600/60 rotate-[30deg]" />
        </div>

        <div className={`${homeRail} relative pt-[8px]`}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_23rem] xl:grid-cols-[214px_minmax(0,1fr)_392px] gap-5 xl:gap-[18px] items-start xl:-mx-[6px]">
            {/* Step index — descriptive only */}
            <ol className="hidden xl:flex flex-col pt-[68px] pl-[2px] list-none" aria-label="Checkout steps">
              {STEPS.map((step, index) => {
                const number = index + 1;
                const current = number === stepReached;
                const done = number < stepReached;
                return (
                  <li key={step.label} className="flex items-start gap-3">
                    <span className="flex flex-col items-center shrink-0">
                      <span
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-bold ${
                          current || done ? 'bg-[#2E8B47] text-white shadow-[0_6px_14px_-6px_rgba(46,139,71,0.7)]' : 'bg-[#EEF3EE] text-[#4B5563]'
                        }`}
                      >
                        {done ? <Check size={16} strokeWidth={3} aria-hidden="true" /> : number}
                      </span>
                      {index < STEPS.length - 1 && <span className="w-px h-[52px] bg-[#DCE8DC]" aria-hidden="true" />}
                    </span>
                    <span className={`rounded-xl px-3 py-2 -mt-1 ${current ? 'bg-[#EAF6E6]' : ''}`}>
                      <span className={`block text-[14px] font-bold leading-tight ${current || done ? 'text-emerald-950' : 'text-[#374151]'}`}>{step.label}</span>
                      <span className="block text-[11.5px] text-ink-secondary mt-[3px] leading-tight">{step.hint}</span>
                    </span>
                  </li>
                );
              })}
            </ol>

            {/* Main column */}
            <div className="min-w-0">
              <h1 className="serif text-[34px] xl:text-[38px] font-bold text-emerald-950 leading-tight">Checkout</h1>
              <p className="text-[14.5px] text-[#4B5563] mt-[6px] mb-[14px]">Complete your order in a few simple steps.</p>

              <div className={`${card} p-5 mb-4`}>
                <CardHeading icon={MapPin} title="Delivery Address" subtitle="Choose a delivery address for your order" />

                {addresses && addresses.length > 0 && (
                  <div className="space-y-2.5 mb-3">
                    {addresses.map((address, index) => {
                      const addressKey = addressKeyOf(address, index);
                      const selected = selectedAddressId === addressKey;
                      return (
                        <label key={addressKey} className={optionCardClass(selected)}>
                          <input
                            type="radio"
                            name="address"
                            className="sr-only"
                            checked={selected}
                            disabled={placing}
                            onChange={() => { setSelectedAddressId(addressKey); setNewAddress(null); setSlotChoice({ key: null, id: null }); setSlotNeedsChoice(null); setNotice(null); resetIdempotencyKey(); }}
                          />
                          <Radio selected={selected} />
                          <span className="w-9 h-9 rounded-full bg-white border border-[#E6EEE6] text-emerald-800 flex items-center justify-center shrink-0" aria-hidden="true">
                            <Home size={17} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[14px] font-bold text-emerald-950 leading-tight">
                              {address.label || 'Address'}
                              {address.isDefault && <span className="ml-1.5 text-[11px] font-medium text-ink-secondary">(Default)</span>}
                            </p>
                            <p className="text-[12.5px] text-[#4B5563] mt-[3px]">
                              {[address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(', ')}
                            </p>
                          </div>
                          {address.isDefault && (
                            <span className="hidden sm:inline-flex items-center h-6 px-3 rounded-full bg-[#DFF3DA] text-emerald-800 text-[11px] font-bold shrink-0">Default</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}

                <details
                  className="mt-2"
                  open={!addresses || addresses.length === 0}
                  onToggle={(e) => { if (e.target.open) { setSelectedAddressId(null); setNewAddress(newAddress || {}); setSlotChoice({ key: null, id: null }); } }}
                >
                  <summary className={`cursor-pointer inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1E7A3C] ${focusRing}`}>
                    <Navigation size={14} aria-hidden="true" />
                    Use a new address
                  </summary>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input placeholder="Address line 1" className={`sm:col-span-2 ${inputClass}`} value={newAddress?.line1 || ''} onChange={handleAddressField('line1')} />
                    <input placeholder="Address line 2 (optional)" className={`sm:col-span-2 ${inputClass}`} value={newAddress?.line2 || ''} onChange={handleAddressField('line2')} />
                    <input placeholder="City" className={inputClass} value={newAddress?.city || ''} onChange={handleAddressField('city')} />
                    <input placeholder="State" className={inputClass} value={newAddress?.state || ''} onChange={handleAddressField('state')} />
                    <input placeholder="Pincode" className={inputClass} value={newAddress?.pincode || ''} onChange={handleAddressField('pincode')} />
                    <input placeholder="Landmark (optional)" className={inputClass} value={newAddress?.landmark || ''} onChange={handleAddressField('landmark')} />
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handlePinLocation}
                      disabled={locating}
                      className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-colors disabled:opacity-60 ${
                        newAddress?.coordinates ? 'border-emerald-700 bg-emerald-50 text-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                      } ${focusRing}`}
                    >
                      <LocateFixed size={16} aria-hidden="true" />
                      {locating ? 'Locating...' : newAddress?.coordinates ? 'Location pinned' : 'Pin my current location'}
                    </button>
                  </div>
                  {locationError && <p className="text-xs text-red-600 mt-2">{locationError}</p>}
                  <p className="text-xs text-ink-secondary mt-2">
                    A pinned location is required for delivery tracking.
                  </p>
                </details>

                {/* Whether this address can be delivered to, as the backend
                    resolves it for the address itself. */}
                <div role="status" aria-live="polite" data-testid="checkout-address-status">
                  {ADDRESS_PROBLEMS[checkout.serviceStatus] && deliveryTarget && (
                    <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                      {ADDRESS_PROBLEMS[checkout.serviceStatus]}
                    </p>
                  )}
                  {checkout.serviceStatus === 'resolving' && deliveryTarget && (
                    <p className="mt-4 text-sm text-ink-secondary">Checking delivery to this address…</p>
                  )}
                  {checkout.serviceStatus === 'unserviceable' && (
                    <p
                      className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3"
                      data-testid="checkout-unserviceable"
                    >
                      We don&apos;t deliver to this address yet. Choose another address to continue.
                    </p>
                  )}
                  {checkout.serviceStatus === 'error' && (
                    <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <p>We couldn&apos;t check delivery to this address.</p>
                      <button type="button" onClick={checkout.retryService} className={`mt-1 font-semibold underline ${focusRing}`}>
                        Try again
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className={`${card} p-5 mb-4`}>
                <CardHeading icon={CreditCard} title="Payment Method" subtitle="Choose how you want to pay" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={`${optionCardClass(effectivePaymentMethod === 'cod', !codAvailable)} !items-start`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      className="sr-only"
                      disabled={!codAvailable || placing}
                      checked={effectivePaymentMethod === 'cod'}
                      onChange={() => { setPaymentMethod('cod'); resetIdempotencyKey(); }}
                    />
                    <Radio selected={effectivePaymentMethod === 'cod'} />
                    <span className="min-w-0">
                      <Banknote size={22} className="text-emerald-700 mb-1.5" aria-hidden="true" />
                      <span className="block text-[14px] font-bold text-emerald-950">Cash on Delivery</span>
                      <span className="block text-[12px] text-ink-secondary mt-[3px]">Pay when you receive your order</span>
                    </span>
                  </label>
                  <label className={`${optionCardClass(effectivePaymentMethod === 'online', hasCatchWeightItem)} !items-start`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      className="sr-only"
                      disabled={hasCatchWeightItem || placing}
                      checked={effectivePaymentMethod === 'online'}
                      onChange={() => { setPaymentMethod('online'); resetIdempotencyKey(); }}
                    />
                    <Radio selected={effectivePaymentMethod === 'online'} />
                    <span className="min-w-0">
                      <CreditCard size={22} className="text-emerald-700 mb-1.5" aria-hidden="true" />
                      <span className="block text-[14px] font-bold text-emerald-950">Pay Online</span>
                      <span className="block text-[12px] text-ink-secondary mt-[3px]">UPI, cards and net banking via Razorpay</span>
                    </span>
                  </label>
                </div>
                {!codAvailable && (
                  <p className="text-xs text-amber-700 mt-2" data-testid="cod-unavailable">
                    {codUnavailableMessage(codReason)}
                  </p>
                )}
                {hasCatchWeightItem && (
                  <p className="text-xs text-amber-700 mt-2">
                    Your cart has a weight-variable item, so online payment isn't available — this order will be Cash on Delivery.
                  </p>
                )}
              </div>

              <div className={`${card} p-5 mb-4`}>
                <CardHeading
                  icon={Clock3}
                  title="Delivery Slot"
                  subtitle="Select a convenient delivery time"
                  action={
                    checkout.slots?.orderingAvailable && checkout.slots?.date ? (
                      <span className="hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-[#DCE8DC] bg-white text-[12.5px] font-bold text-emerald-950">
                        <CalendarDays size={15} className="text-emerald-700" aria-hidden="true" />
                        {checkout.slots.date}
                      </span>
                    ) : null
                  }
                />
                {checkout.serviceStatus !== 'serviceable' && (
                  <p className="text-sm text-ink-secondary">
                    Delivery times appear once we&apos;ve confirmed we deliver to your address.
                  </p>
                )}
                {checkout.slotsStatus === 'loading' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="slots-loading">
                    <SkeletonBlock className="h-12" />
                    <SkeletonBlock className="h-12" />
                    <SkeletonBlock className="h-12" />
                  </div>
                )}
                {checkout.slotsStatus === 'error' && (
                  <div role="alert" className="text-sm text-red-700">
                    <p>We couldn&apos;t load delivery times for this address.</p>
                    <button type="button" onClick={checkout.retrySlots} className={`mt-1 font-semibold underline ${focusRing}`}>
                      Try again
                    </button>
                  </div>
                )}
                {checkout.slots?.orderingAvailable === false && (
                  <p className="text-sm text-amber-700">
                    {checkout.slots.unavailableReason?.message || 'Delivery slots are currently unavailable.'}
                  </p>
                )}
                {checkout.slots?.orderingAvailable && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="delivery-slots">
                    {slotList.map((slot) => {
                      const selected = selectedSlotId === slot.id;
                      const Glyph = slotIcon(slot);
                      return (
                        <label
                          key={slot.id}
                          className={`flex items-center justify-center gap-2 text-center h-[40px] px-3 rounded-lg border text-[13px] font-medium transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-emerald-700 has-[:focus-visible]:outline-offset-2 ${
                            !slot.available
                              ? 'opacity-50 cursor-not-allowed border-[#E6EEE6] text-gray-400'
                              : selected
                                ? 'border-[#2E8B47] bg-[#F1FAEF] text-emerald-950 font-bold ring-1 ring-[#2E8B47] cursor-pointer'
                                : 'border-[#E6EEE6] text-emerald-950 hover:border-emerald-200 cursor-pointer'
                          }`}
                        >
                          <input
                            type="radio"
                            name="deliverySlot"
                            className="sr-only"
                            disabled={!slot.available || placing}
                            checked={selected}
                            onChange={() => { setSlotChoice({ key: checkout.slotsKey, id: slot.id }); setSlotNeedsChoice(null); resetIdempotencyKey(); }}
                          />
                          {selected ? (
                            <CheckCircle2 size={14} className="text-emerald-700 shrink-0" aria-hidden="true" />
                          ) : Glyph ? (
                            <Glyph size={14} className="text-[#E3A008] shrink-0" aria-hidden="true" />
                          ) : null}
                          {slot.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* What the last attempt came back with, when the customer must act. */}
              {stockBlocked && (
                <div role="alert" className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4" data-testid="stock-conflict">
                  <p className="font-semibold">Some items are no longer available in the quantity you chose.</p>
                  <ul className="mt-1 space-y-0.5">
                    {stockConflict.items.map((line) => (
                      <li key={line.productId}>
                        {line.productName}: {line.availableQty > 0 ? `only ${line.availableQty} available` : 'out of stock'} (you asked for {line.requestedQty})
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1">
                    <Link to="/cart" className={`font-semibold underline ${focusRing}`}>Update your cart</Link> to continue. Nothing was charged.
                  </p>
                </div>
              )}
              {notice && (
                <div role="alert" className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4" data-testid="placement-notice">
                  {notice.kind === 'quote_changed' ? (
                    <>
                      <p className="font-semibold">{notice.reason}</p>
                      <p className="mt-0.5">
                        {notice.newTotal !== null && notice.previousTotal !== null && notice.newTotal !== notice.previousTotal
                          ? `Your total changed from ${formatRupees(notice.previousTotal)} to ${formatRupees(notice.newTotal)}. `
                          : notice.newTotal !== null
                          ? `Your total is ${formatRupees(notice.newTotal)}. `
                          : ''}
                        Nothing was charged. Please review your order and place it again.
                      </p>
                    </>
                  ) : (
                    <p>{notice.message}</p>
                  )}
                </div>
              )}
              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

              {unresolvedAttempt && !pendingOrder && (
                <div role="status" className={`${card} p-5 mb-4`} data-testid="unresolved-attempt">
                  <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">
                    {unresolvedAttempt.saleId ? 'Your last order is waiting for payment' : "We couldn't confirm your last order"}
                  </h2>
                  <p className="text-sm text-ink-secondary mb-3">
                    {unresolvedAttempt.saleId
                      ? 'Continue to pay for it. You will not be charged twice.'
                      : 'Check it before placing another. It will not be placed twice.'}
                  </p>
                  <Button onClick={handleRecoverAttempt} loading={placing} loadingLabel="Checking..." disabled={placing || recoveryHeld}>
                    {unresolvedAttempt.saleId ? 'Continue payment' : 'Check my last order'}
                  </Button>
                </div>
              )}
              {pendingOrder && (
                <div role="status" className={`${card} p-5 mb-4`} data-testid="pending-order">
                  {pendingOrder.status === 'verifying' && (
                    <p className="text-sm text-ink-secondary">Checking on your earlier order…</p>
                  )}
                  {pendingOrder.status === 'pending' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">We&apos;re confirming a store for your order…</h2>
                      <p className="text-sm text-ink-secondary">
                        {pendingOrder.message ||
                          "We've received your order and are arranging a store to fulfil it. You won't be charged until it's confirmed."}
                      </p>
                      <button type="button" onClick={() => checkPendingOrder()} className={`mt-3 text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                        Check status
                      </button>
                    </>
                  )}
                  {pendingOrder.status === 'ready' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">{pendingOrder.checkoutGroupId ? (pendingOrder.paymentMethod === 'online' ? 'Your orders are ready for payment' : 'Complete your orders') : 'A store has confirmed your order'}</h2>
                      <p className="text-sm text-ink-secondary mb-3">Continue to complete your order.</p>
                      <Button onClick={handleContinuePending} loading={placing} loadingLabel="Continuing..." disabled={placing}>
                        Continue
                      </Button>
                    </>
                  )}
                  {pendingOrder.status === 'finalizing' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">{pendingOrder.checkoutGroupId ? 'Confirming your orders' : "We've received your payment"}</h2>
                      <p className="text-sm text-ink-secondary">We&apos;re checking the outcome before you can place another order.</p>
                      <button type="button" onClick={() => checkPendingOrder()} className={`mt-3 text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                        Check status
                      </button>
                    </>
                  )}
                  {pendingOrder.status === 'missing' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">Check your last checkout</h2>
                      <Button onClick={handleRecoverAttempt} loading={placing} loadingLabel="Checking..." disabled={placing || recoveryHeld}>
                        Check my last order
                      </Button>
                    </>
                  )}
                  {pendingOrder.status === 'expiring' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">The time to pay for this order has run out</h2>
                      <p className="text-sm text-ink-secondary">We&apos;re updating this order. You can return to checkout as soon as that&apos;s done.</p>
                      <button type="button" onClick={() => checkPendingOrder()} className={`mt-3 text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                        Check status
                      </button>
                    </>
                  )}
                  {pendingOrder.status === 'expired' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">The time to pay for this order ran out</h2>
                      <p className="text-sm text-ink-secondary mb-3">It was cancelled and won&apos;t be delivered. Your cart is still here.</p>
                      <button type="button" onClick={() => forgetPendingOrder(pendingOrder.saleId)} className={`text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                        Back to checkout
                      </button>
                    </>
                  )}
                  {pendingOrder.status === 'failed' && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">We couldn&apos;t arrange a store for this order</h2>
                      <p className="text-sm text-ink-secondary mb-3">It wasn&apos;t placed and you haven&apos;t been charged. Your cart is still here.</p>
                      <button type="button" onClick={() => forgetPendingOrder(pendingOrder.saleId)} className={`text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                        Back to checkout
                      </button>
                    </>
                  )}
                  {REFUND_MESSAGES[pendingOrder.status] && (
                    <>
                      <h2 className="serif text-[20px] font-bold text-emerald-950 mb-2">
                        {pendingOrder.status === 'refunded' ? 'Your payment has been refunded' : 'Your refund is being processed'}
                      </h2>
                      <p className="text-sm text-ink-secondary mb-3">{REFUND_MESSAGES[pendingOrder.status]}</p>
                      <div className="flex flex-wrap gap-4">
                        <Link to={`/orders/${pendingOrder.saleId}`} className={`text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                          View order
                        </Link>
                        <button type="button" onClick={() => forgetPendingOrder(pendingOrder.saleId)} className={`text-sm font-semibold text-emerald-800 underline ${focusRing}`}>
                          Back to checkout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <Button
                size="lg"
                onClick={handlePlaceOrder}
                disabled={placing || !checkoutReady}
                aria-describedby={checkoutBlocker ? 'checkout-place-blocker' : undefined}
                loading={placing}
                loadingLabel="Placing order..."
                className="w-full !h-[44px] !text-[15px]"
              >
                <Lock size={16} aria-hidden="true" />
                Place Order
                {checkout.expectedGrandTotal !== null && (
                  <span className="ml-1 font-bold" data-testid="place-order-total" aria-hidden="true">{formatRupees(checkout.expectedGrandTotal)}</span>
                )}
              </Button>
              {placing && submittedTotal !== null && (
                <p className="text-xs text-ink-secondary text-center mt-2" data-testid="submitting-total">
                  Placing your order for {formatRupees(submittedTotal)}…
                </p>
              )}
              {!placing && checkoutBlocker && (
                <p id="checkout-place-blocker" className="text-xs text-ink-secondary text-center mt-2">
                  {checkoutBlocker}
                </p>
              )}
            </div>

            {/* Order summary — the order as the backend prices it for THIS
                address: its own quote, never the cart's, never local arithmetic. */}
            <aside className="lg:sticky lg:top-24 space-y-3">
              <div className={`${card} p-5`} data-testid="checkout-summary">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h2 className="flex items-center gap-2.5 serif text-[20px] font-bold text-emerald-950">
                    <ShoppingCart size={20} className="text-emerald-700" aria-hidden="true" />
                    Order Summary
                  </h2>
                  <span className="text-[12.5px] font-semibold text-ink-secondary">{items.length} Item{items.length === 1 ? '' : 's'}</span>
                </div>
                {checkout.serviceStatus !== 'serviceable' && (
                  <p className="text-sm text-ink-secondary">
                    Your total appears once we&apos;ve confirmed we deliver to your address.
                  </p>
                )}
                {checkout.quoteStatus === 'loading' && (
                  <div className="space-y-3" data-testid="checkout-quote-loading">
                    <SkeletonBlock className="h-4 w-full" />
                    <SkeletonBlock className="h-4 w-full" />
                    <SkeletonBlock className="h-8 w-full" />
                  </div>
                )}
                {checkout.quoteStatus === 'error' && (
                  <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <p className="font-semibold">We couldn&apos;t price your order for this address.</p>
                    {checkout.quoteError?.message && <p className="mt-0.5">{checkout.quoteError.message}</p>}
                    <button type="button" onClick={checkout.retryQuote} className={`mt-2 font-semibold underline ${focusRing}`}>
                      Try again
                    </button>
                  </div>
                )}
                {quote && (
                  <>
                    <ul className="divide-y divide-[#EEF3EE] mb-4" data-testid="checkout-items">
                      {items.map((item) => {
                        const line = quoteLineFor(item.productId);
                        const issue = issueFor(item.productId);
                        return (
                          <li key={item.productId} className="flex items-center gap-3 py-2.5 text-sm">
                            <span className="w-[54px] h-[54px] rounded-xl overflow-hidden bg-[#F4F7F3] flex items-center justify-center shrink-0">
                              {item.product?.thumbnail ? (
                                <img src={item.product.thumbnail} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <ImageOff size={18} className="text-emerald-300" aria-hidden="true" />
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-[13.5px] font-bold text-emerald-950 break-words leading-snug">
                                {item.product?.displayName || 'Product'} × {item.quantity}
                              </span>
                              {item.product?.sellingUom && (
                                <span className="block text-[12px] text-ink-secondary mt-[2px]">{item.product.sellingUom}</span>
                              )}
                              {issue && <span className="block text-xs text-amber-700 mt-0.5">{issue.message}</span>}
                              {!line && !issue && (
                                <span className="block text-xs text-amber-700 mt-0.5">
                                  We couldn&apos;t price this item for this address.
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 text-[14px] font-bold text-emerald-950">
                              {line ? formatRupees(line.lineTotal) : '—'}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {(lineProblems.length > 0 || unpricedLines.length > 0) && (
                      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4">
                        Some items can&apos;t be delivered to this address as they are.{' '}
                        <Link to="/cart" className={`font-semibold underline ${focusRing}`}>Update your cart</Link> or choose
                        another address.
                      </p>
                    )}
                    {basketIssues.map((issue) => (
                      <p
                        key={issue.type || issue.message}
                        className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4"
                      >
                        {issue.message}
                      </p>
                    ))}

                    {/* The carried coupon, as THIS quote judged it. */}
                    {couponView && (
                      <div role="status" className="text-sm rounded-xl border border-[#E6EEE6] px-4 py-3 mb-4" data-testid="checkout-coupon">
                        {couponView.state === 'applied' && (
                          <p className="text-emerald-800">
                            {checkout.couponCode} applied to this order.
                            {couponView.freeDelivery
                              ? ' Free delivery.'
                              : couponView.savings !== null
                              ? ` You save ${formatRupees(couponView.savings)}.`
                              : ''}
                          </p>
                        )}
                        {couponView.state === 'refused' && (
                          <p className="text-amber-900">
                            {checkout.couponCode} isn&apos;t applied to this order. {couponView.message}
                          </p>
                        )}
                        {couponView.state === 'unchecked' && (
                          <p className="text-ink-secondary">We couldn&apos;t check {checkout.couponCode} for this order.</p>
                        )}
                        <p className="text-xs text-ink-secondary mt-1">
                          Codes are checked again for your delivery address, so this can differ from your cart.{' '}
                          <Link to="/cart" className={`font-semibold underline ${focusRing}`}>Change it in your cart</Link>
                        </p>
                      </div>
                    )}

                    <h3 className="serif text-[17px] font-bold text-emerald-950 mb-3">Price Details</h3>
                    <QuoteSummary quote={quote} itemCount={items.length} couponCode={checkout.couponCode} cartItems={items} />

                    {quote.binding === false && (
                      <p className="text-xs text-ink-secondary -mt-2">
                        Indicative pricing. Your final total is confirmed when the order is placed.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="rounded-2xl bg-[#EAF6E6] border border-[#DFF3DA] p-4 flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#2E8B47] text-white flex items-center justify-center shrink-0" aria-hidden="true">
                  <ShieldCheck size={22} />
                </span>
                <span>
                  <span className="block text-[13.5px] font-bold text-emerald-950">Secure payments</span>
                  <span className="block text-[12px] text-[#4B5563] mt-[3px] leading-[1.4]">Online payments are processed by Razorpay; cash is paid only on delivery.</span>
                </span>
              </div>

              <ul className={`${card} grid grid-cols-3 divide-x divide-[#EEF3EE] py-4`}>
                {ASSURANCES.map(({ icon: Icon, title, subtitle }) => (
                  <li key={title} className="flex flex-col items-center text-center px-2">
                    <span className="w-10 h-10 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center">
                      <Icon size={18} aria-hidden="true" />
                    </span>
                    <span className="mt-2 text-[12px] font-bold text-emerald-950 leading-tight">{title}</span>
                    <span className="text-[11px] text-ink-secondary mt-[2px] leading-tight">{subtitle}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CheckoutPage;
